import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Check, 
  BarChart3, 
  Brain, 
  Globe, 
  Shield, 
  Zap,
  Users,
  Sparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import PublicLayout from '../components/layout/PublicLayout';
import { cn } from '../lib/utils';
import { subscriptionApi } from '../lib/api';

interface ApiPlan {
  id: string;
  name: string;
  price: number;
  annualPrice?: number;
  currency: string;
}

interface LandingPricingPlan {
  id: string;
  nameKey: string;
  descKey: string;
  price: { monthly: number; yearly: number };
  currency: string;
  features: string[];
  popular: boolean;
}

const fallbackPlans: LandingPricingPlan[] = [
  {
    id: 'basic',
    nameKey: 'pricing.basic.name',
    descKey: 'pricing.basic.desc',
    price: { monthly: 399, yearly: 3590 },
    currency: 'TRY',
    features: [
      'pricing.basic.feature1',
      'pricing.basic.feature2',
      'pricing.basic.feature3',
      'pricing.basic.feature4',
      'pricing.basic.feature5'
    ],
    popular: false
  },
  {
    id: 'professional',
    nameKey: 'pricing.professional.name',
    descKey: 'pricing.professional.desc',
    price: { monthly: 999, yearly: 9590 },
    currency: 'TRY',
    features: [
      'pricing.professional.feature1',
      'pricing.professional.feature2',
      'pricing.professional.feature3',
      'pricing.professional.feature4',
      'pricing.professional.feature5',
      'pricing.professional.feature6'
    ],
    popular: true
  },
  {
    id: 'agency',
    nameKey: 'pricing.enterprise.name',
    descKey: 'pricing.enterprise.desc',
    price: { monthly: 2499, yearly: 24990 },
    currency: 'TRY',
    features: [
      'pricing.enterprise.feature1',
      'pricing.enterprise.feature2',
      'pricing.enterprise.feature3',
      'pricing.enterprise.feature5',
      'pricing.enterprise.feature6'
    ],
    popular: false
  }
];

const LandingPage: React.FC = () => {
  const { t } = useLanguage();
  const [isYearly, setIsYearly] = useState(false);
  const [apiPlans, setApiPlans] = useState<ApiPlan[]>([]);

  const features = [
    {
      icon: Brain,
      titleKey: 'features.ai.title',
      descKey: 'features.ai.desc',
    },
    {
      icon: Globe,
      titleKey: 'features.platform.title',
      descKey: 'features.platform.desc',
    },
    {
      icon: BarChart3,
      titleKey: 'features.analytics.title',
      descKey: 'features.analytics.desc',
    },
    {
      icon: Shield,
      titleKey: 'features.security.title',
      descKey: 'features.security.desc',
    },
    {
      icon: Zap,
      titleKey: 'features.realtime.title',
      descKey: 'features.realtime.desc',
    },
    {
      icon: Users,
      titleKey: 'features.team.title',
      descKey: 'features.team.desc',
    }
  ];

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const response = await subscriptionApi.getPlans();
        if (Array.isArray(response)) {
          setApiPlans(response);
        }
      } catch {
        setApiPlans([]);
      }
    };

    loadPlans();
  }, []);

  const pricingPlans = useMemo(() => {
    if (!apiPlans.length) {
      return fallbackPlans;
    }

    const uiMetaByPlanId: Record<string, Pick<LandingPricingPlan, 'nameKey' | 'descKey' | 'features' | 'popular'>> = {
      basic: {
        nameKey: 'pricing.basic.name',
        descKey: 'pricing.basic.desc',
        features: ['pricing.basic.feature1', 'pricing.basic.feature2', 'pricing.basic.feature3', 'pricing.basic.feature4', 'pricing.basic.feature5'],
        popular: false,
      },
      professional: {
        nameKey: 'pricing.professional.name',
        descKey: 'pricing.professional.desc',
        features: ['pricing.professional.feature1', 'pricing.professional.feature2', 'pricing.professional.feature3', 'pricing.professional.feature4', 'pricing.professional.feature5', 'pricing.professional.feature6'],
        popular: true,
      },
      agency: {
        nameKey: 'pricing.enterprise.name',
        descKey: 'pricing.enterprise.desc',
        features: ['pricing.enterprise.feature1', 'pricing.enterprise.feature2', 'pricing.enterprise.feature3', 'pricing.enterprise.feature4', 'pricing.enterprise.feature5', 'pricing.enterprise.feature6', 'pricing.enterprise.feature7'],
        popular: false,
      },
    };

    return apiPlans
      .filter(plan => uiMetaByPlanId[plan.id?.toLowerCase()])
      .map(plan => {
        const id = plan.id.toLowerCase();
        const meta = uiMetaByPlanId[id];
        const annualPrice = plan.annualPrice ?? (plan.price * 12);

        return {
          id,
          nameKey: meta.nameKey,
          descKey: meta.descKey,
          features: meta.features,
          popular: meta.popular,
          currency: plan.currency || 'TRY',
          price: {
            monthly: plan.price,
            yearly: annualPrice,
          },
        };
      });
  }, [apiPlans]);

  const formatPrice = (amount: number, currency: string) => {
    if (currency?.toUpperCase() === 'TRY') {
      return `₺${amount}`;
    }
    if (currency?.toUpperCase() === 'USD') {
      return `$${amount}`;
    }
    return `${amount} ${currency}`;
  };

  return (
    <PublicLayout>
      <Helmet>
        <title>Revomira — Google Yorumlarınızı Yönetin | AI Destekli</title>
        <meta
          name="description"
          content="Türkiye'deki restoran, otel ve perakende zincirleri için Google yorum yönetimi, AI analizi ve tek tıkla yanıt."
        />
        <meta name="robots" content="index,follow" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-12 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="secondary" className="mb-4 text-sm px-4 py-1">
                <Sparkles className="h-3 w-3 mr-1" />
                {t('hero.badge')}
              </Badge>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-tight">
                {t('hero.title')}
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
                {t('hero.subtitle')}
              </p>
              <p className="text-base md:text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                {t('hero.subcopy')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="xl" className="gap-2">
                  <Link to="/register">
                    {t('hero.cta.start')}
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="xl" variant="outline">
                  <a href="#features">{t('hero.cta.demo')}</a>
                </Button>
              </div>
              {/* Realistic mini dashboard preview */}
              <div className="relative mt-12 max-w-4xl mx-auto text-left">
                {/* Glow behind the preview */}
                <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 via-purple-500/10 to-primary/20 blur-3xl rounded-3xl" />

                <div className="relative rounded-2xl border border-border/60 bg-card shadow-2xl overflow-hidden">
                  {/* Browser chrome */}
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-secondary/30">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                    <div className="flex-1 mx-3 h-5 rounded-md bg-border/60 text-xs flex items-center justify-center text-muted-foreground">
                      app.revomira.com/dashboard
                    </div>
                  </div>

                  {/* Mock dashboard */}
                  <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { label: t('landing.preview.reviews'), value: '1,248', change: '+12%', wrap: 'bg-primary/10 border-primary/20' },
                      { label: t('landing.preview.avgRating'), value: '4.6★', change: '+0.2', wrap: 'bg-emerald-500/10 border-emerald-500/20' },
                      { label: t('landing.preview.responseRate'), value: '92%', change: '+8%', wrap: 'bg-purple-500/10 border-purple-500/20' },
                      { label: t('landing.preview.businesses'), value: '12', change: '+3', wrap: 'bg-amber-500/10 border-amber-500/20' },
                    ].map((stat) => (
                      <div key={stat.label} className={cn('rounded-xl p-3 border', stat.wrap)}>
                        <p className="text-xs text-muted-foreground mb-1">{stat.label}</p>
                        <p className="text-xl font-bold tabular-nums">{stat.value}</p>
                        <p className="text-xs text-emerald-500 font-medium mt-0.5">{stat.change}</p>
                      </div>
                    ))}

                    {/* Mini review list preview */}
                    <div className="col-span-2 sm:col-span-4 mt-1 space-y-2">
                      {[
                        { text: 'Harika hizmet, kesinlikle tavsiye ederim...', rating: 5, sentiment: 'positive' },
                        { text: 'Beklentilerimin altında kaldı, iyileştirme gerekiyor...', rating: 2, sentiment: 'negative' },
                        { text: 'Genel olarak memnunum, tekrar gelebilirim...', rating: 4, sentiment: 'neutral' },
                      ].map((r, i) => (
                        <div
                          key={i}
                          className={cn(
                            'flex items-center gap-3 p-2.5 rounded-lg bg-secondary/40 text-xs',
                            `sentiment-${r.sentiment}`
                          )}
                        >
                          <div className="text-amber-400">{'★'.repeat(r.rating)}</div>
                          <p className="flex-1 text-muted-foreground truncate">{r.text}</p>
                          <span
                            className={cn(
                              'px-2 py-0.5 rounded-full text-[10px] font-medium',
                              r.sentiment === 'positive'
                                ? 'bg-emerald-500/20 text-emerald-500'
                                : r.sentiment === 'negative'
                                  ? 'bg-red-500/20 text-red-500'
                                  : 'bg-slate-500/20 text-slate-500'
                            )}
                          >
                            {t(`review.${r.sentiment}`)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t('features.title')}
            </h2>
            <p className="text-xl text-muted-foreground">
              {t('features.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <feature.icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{t(feature.titleKey)}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base">
                      {t(feature.descKey)}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/40">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t('landing.howItWorks.title')}
            </h2>
            <p className="text-xl text-muted-foreground">{t('landing.howItWorks.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { titleKey: 'landing.howItWorks.step1.title', descKey: 'landing.howItWorks.step1.desc' },
              { titleKey: 'landing.howItWorks.step2.title', descKey: 'landing.howItWorks.step2.desc' },
              { titleKey: 'landing.howItWorks.step3.title', descKey: 'landing.howItWorks.step3.desc' },
            ].map((step, index) => (
              <Card key={step.titleKey}>
                <CardHeader>
                  <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold mb-2">
                    {index + 1}
                  </div>
                  <CardTitle>{t(step.titleKey)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">{t(step.descKey)}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t('pricing.title')}
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              {t('pricing.subtitle')}
            </p>
            <div className="flex items-center justify-center space-x-4">
              <span className={`text-sm ${!isYearly ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                {t('pricing.monthly')}
              </span>
              <button
                onClick={() => setIsYearly(!isYearly)}
                className="relative inline-flex h-6 w-11 items-center rounded-full bg-primary transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-primary-foreground transition-transform ${
                    isYearly ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm ${isYearly ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                {t('pricing.yearly')}
              </span>
              {isYearly && (
                <Badge variant="secondary" className="ml-2">
                  {t('pricing.save')}
                </Badge>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={plan.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className={`h-full relative transition-all duration-300 hover:shadow-xl ${
                  plan.popular ? 'ring-2 ring-primary shadow-lg scale-105' : ''
                }`}>
                  {plan.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground">
                        {t('pricing.mostPopular')}
                      </Badge>
                    </div>
                  )}
                  <CardHeader className="text-center">
                    <CardTitle className="text-2xl">{t(plan.nameKey)}</CardTitle>
                    <div className="mt-4">
                      <span className="text-4xl font-bold text-foreground">
                        {formatPrice(isYearly ? plan.price.yearly : plan.price.monthly, plan.currency)}
                      </span>
                      <span className="text-muted-foreground">
                        /{isYearly ? t('pricing.year') : t('pricing.month')}
                      </span>
                    </div>
                    <CardDescription className="mt-2">{t(plan.descKey)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 mb-6">
                      {plan.features.map((featureKey, featureIndex) => (
                        <li key={featureIndex} className="flex items-center">
                          <Check className="h-5 w-5 text-success mr-3 flex-shrink-0" />
                          <span className="text-muted-foreground">{t(featureKey)}</span>
                        </li>
                      ))}
                    </ul>
                    <Button
                      asChild
                      variant={plan.popular ? 'default' : 'secondary'}
                      size="xl"
                      className="w-full"
                    >
                      <Link to="/register">{t('pricing.getStarted')}</Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pilot Program Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-muted/40 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t('landing.pilot.title')}
            </h2>
            <p className="text-xl text-muted-foreground">{t('landing.pilot.subtitle')}</p>
          </div>
          <Button asChild size="xl" className="gap-2">
            <Link to="/register">
              {t('landing.pilot.cta')}
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-primary to-primary/80">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">
              {t('cta.title')}
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-8">
              {t('cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="xl" className="bg-background text-foreground hover:bg-background/90">
                <Link to="/register">{t('cta.start')}</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="xl"
                className="border-2 border-primary-foreground/60 bg-transparent text-primary-foreground hover:bg-primary-foreground hover:text-primary"
              >
                <Link to="/contact">{t('cta.contact')}</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default LandingPage;
